import { makeAutoObservable } from 'mobx';
import { parseRawBoolean } from '@data/api/helpers';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
import { normalizeAmenitiesFromDTO } from '@business/utils/amenities';
import Image from './Image';
import MultilangText from './MultilangText';
import Settings from './Settings';
function getLogoUrl({ booking_display_photos, booking_logo, logo_original, }) {
    if (parseRawBoolean(booking_display_photos)) {
        return logo_original;
    }
    else if (booking_logo) {
        return booking_logo === null || booking_logo === void 0 ? void 0 : booking_logo.path;
    }
    else {
        return null;
    }
}
function formatTime(time24, timeFormat) {
    if (!time24) {
        return '';
    }
    const timeParts = time24.split(':');
    const useMilitaryTime = timeFormat === 'H:i';
    if (useMilitaryTime || timeParts.length !== 2) {
        return time24;
    }
    let hours = parseInt(timeParts[0], 10);
    const minutes = String(parseInt(timeParts[1], 10)).padStart(2, '0');
    const amOrPm = hours >= 12 ? 'PM' : 'AM';
    if (hours >= 13) {
        hours = hours - 12;
    }
    if (hours === 0) {
        hours = 12;
    }
    return `${hours}:${minutes} ${amOrPm}`;
}
function parsePaymentGatewayFields(paymentGatewayData) {
    if (!paymentGatewayData) {
        return [];
    }
    const fields = [];
    const keys = Object.keys(paymentGatewayData.billing_address_fields);
    const configurationKeys = Object.keys(paymentGatewayData.fields_property_configuration);
    keys.forEach((name) => {
        const billingAddressField = paymentGatewayData.billing_address_fields[name];
        const label = billingAddressField.label;
        const display = configurationKeys.includes(name + '_display');
        const required = configurationKeys.includes(name + '_mandatory');
        const field = {
            label,
            name,
            required,
        };
        if (billingAddressField.countryBasedOptions) {
            const contryBasedKeys = Object.keys(billingAddressField.countryBasedOptions);
            field.options = Object.entries(billingAddressField.countryBasedOptions[contryBasedKeys[0]]).map(([value, label]) => ({ label, value }));
        }
        if (display) {
            fields.push(field);
        }
    });
    return fields;
}
export default class Property {
    constructor(serverData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
        Object.defineProperty(this, "additionalAmenities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "address", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "allowSameDayReservation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "amenities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "amplifyAllowGA4PurchaseEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "amplifyFeatures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "bankTransferInstructions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cancellationPolicies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cancellationPolicy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "code", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "colors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "contact", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "coverImage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "customFields", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "customFooter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "customHeader", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultCurrency", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultLanguage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deposit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "description", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "externalOffersEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "facebookPixelId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "formats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "googleConversionId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "googleConversionLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "images", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "immersiveExperienceActivated", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "legacyAdditionalAmenities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "legacyAmenities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "noAvailabilityText", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "notFound", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "paymentAPIKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "paymentGateway", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "paymentMethods", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "picture", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "policies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "privacyPolicy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "roomsSourceSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "settings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "startOfWeek", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "termsAndConditions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeAutoObservable(this);
        if (!serverData || !serverData.success || !serverData.data) {
            this.id = 0;
            this.immersiveExperienceActivated = true;
            this.notFound = true;
            this.startOfWeek = 0;
            return;
        }
        const { data } = serverData;
        this.id = parseInt(data.property_id);
        this.code = data.property_code;
        this.name = data.hotel_name;
        this.picture = new Image({
            alt: data.hotel_name,
            src: getLogoUrl(data),
        });
        this.coverImage = new Image({
            alt: data.hotel_name,
            src: data.logo_original,
            thumb: data.logo,
        });
        this.description = new MultilangText(data.hotel_about_langs);
        this.address = data.hotel_address;
        this.images = (data.additional_photos || []).map((image) => new Image({ alt: image.alt, src: image.path, thumb: image.thumb_path }));
        this.cancellationPolicy = new MultilangText(data.custom_cancellation_policy_langs);
        this.termsAndConditions = new MultilangText(data.terms_txt_langs);
        // Resilient to the field being absent (pre-backend-launch): MultilangText
        // yields an empty instance whose getText() returns undefined (BENG-4195).
        this.privacyPolicy = new MultilangText(data.privacy_policy_txt_langs);
        this.allowSameDayReservation = parseRawBoolean(data.same_day_reservation);
        this.bankTransferInstructions = new MultilangText(data.bank_transfer_instruction);
        this.customHeader = ((_a = data === null || data === void 0 ? void 0 : data.custom) === null || _a === void 0 ? void 0 : _a.header) || '';
        this.customFooter = ((_b = data === null || data === void 0 ? void 0 : data.custom) === null || _b === void 0 ? void 0 : _b.footer) || '';
        // colors
        this.colors = {
            background: ((_d = (_c = data.custom) === null || _c === void 0 ? void 0 : _c.colors) === null || _d === void 0 ? void 0 : _d.background) || '#F6F5F5',
            primary: ((_f = (_e = data.custom) === null || _e === void 0 ? void 0 : _e.colors) === null || _f === void 0 ? void 0 : _f.searchButton) || '#6A5ACD',
        };
        if (((_g = data.custom) === null || _g === void 0 ? void 0 : _g.no_availability_text) &&
            Object.keys((_h = data.custom) === null || _h === void 0 ? void 0 : _h.no_availability_text).length > 0) {
            this.noAvailabilityText = new MultilangText((_j = data.custom) === null || _j === void 0 ? void 0 : _j.no_availability_text);
        }
        this.paymentAPIKey = data.payment_hash;
        this.facebookPixelId = data.facebook_pixel_id || null;
        this.googleConversionId = data.google_conversion_id;
        this.googleConversionLabel = data.google_conversion_label;
        if (!data.payment_gateway_settings ||
            data.payment_gateway_settings.gateway_connected === '') {
            this.paymentGateway = {
                fields: [],
                name: data.payment_gateway,
            };
        }
        else {
            this.paymentGateway = {
                fields: parsePaymentGatewayFields(data.payment_gateway_settings),
                name: data.payment_gateway_settings.gateway_connected,
            };
        }
        this.externalOffersEnabled = data.external_offers_enabled || false;
        const creditCardsEnabled = data.credit_cards_enabled || false;
        const showCreditCardsInFrontend = parseRawBoolean(data.cards_frontend_enabled);
        this.paymentMethods = {
            bankTransfersEnabled: data.bank_transfers_enabled || false,
            creditCardsEnabled: creditCardsEnabled && showCreditCardsInFrontend,
            paypalEnabled: data.paypal_enabled || false,
            supportedCards: data.payments_credit_cards.map(({ payment_code, payment_name }) => ({
                code: payment_code,
                name: payment_name,
            })),
        };
        let defaultCurrency = (_k = (data.default_currency || '')) === null || _k === void 0 ? void 0 : _k.toLowerCase();
        if (FeatureFlagManager.isEnabled(FeatureFlag.disableBulgarianCurrency) &&
            defaultCurrency === 'bgn') {
            defaultCurrency = 'eur';
        }
        this.defaultCurrency = defaultCurrency || 'usd';
        this.defaultLanguage =
            ((_l = data.default_lang) === null || _l === void 0 ? void 0 : _l.toLowerCase()) || 'en';
        this.cancellationPolicies = {
            customPolicy: new MultilangText(data.custom_cancellation_policy_langs),
            standardizedPolicies: {
                fullCharge: {
                    allowed: parseRawBoolean(data.terms_full_charge_allowed),
                    chargeType: data.terms_full_charge_type,
                    daysBeforeArrival: data.terms_full_charge_cancellation_days,
                },
                noCharge: {
                    allowed: parseRawBoolean(data.terms_no_charge_allowed),
                    daysBeforeArrival: data.terms_no_charge_cancellation_days,
                },
                partialCharge: {
                    allowed: parseRawBoolean(data.terms_partial_charge_allowed),
                    chargeAmount: data.terms_partial_amount,
                    chargeType: data.terms_partial_charge_type,
                    daysBeforeArrival: data.terms_partial_charge_cancellation_days,
                },
            },
            useCustomPolicy: data.is_own_cancellation === '1',
        };
        this.customFields = [];
        const customFields = data.custom_fields || {};
        Object.keys(customFields).forEach((key) => {
            var _a;
            const field = customFields[key];
            if ((_a = field === null || field === void 0 ? void 0 : field.location) === null || _a === void 0 ? void 0 : _a.includes('booking')) {
                this.customFields.push({
                    label: new MultilangText(field.label_langs),
                    max_length: parseInt(field.max_len) || 255,
                    name: key,
                    required: parseRawBoolean(field.required),
                    section: field.section,
                    type: field.type,
                });
            }
        });
        this.amenities = !data.amenities
            ? undefined
            : normalizeAmenitiesFromDTO(data.amenities);
        this.additionalAmenities = !data.customAmenities
            ? undefined
            : data.customAmenities.map((name) => ({
                category: 'additional-amenity',
                name,
            }));
        this.legacyAmenities = Object.values((_m = data.hotel_features) !== null && _m !== void 0 ? _m : {}).map(({ slug }) => ({
            code: slug,
        }));
        const legacyAdditionalAmenities = Array.isArray(data.hotel_features_custom)
            ? {}
            : (_o = data.hotel_features_custom) !== null && _o !== void 0 ? _o : {};
        this.legacyAdditionalAmenities = Object.keys(legacyAdditionalAmenities).map((name) => ({
            category: 'additional-amenity',
            name,
        }));
        this.contact = {
            email: data.hotel_email,
            firstName: data.contact_first_name,
            lastName: data.contact_last_name,
            phone: data.hotel_phone,
        };
        this.policies = {
            checkin: formatTime(data.terms_normal_checkin, (_p = data.formats) === null || _p === void 0 ? void 0 : _p.time_format),
            checkout: formatTime(data.terms_normal_checkout, (_q = data.formats) === null || _q === void 0 ? void 0 : _q.time_format),
            lateCheckout: parseRawBoolean(data.terms_late_checkout),
            lateCheckoutHour: formatTime(data.terms_late_checkout_hour, (_r = data.formats) === null || _r === void 0 ? void 0 : _r.time_format),
            lateCheckoutType: data.terms_late_checkout_type,
            lateCheckoutVal: parseFloat(data.terms_late_checkout_val),
        };
        this.settings = new Settings(data);
        const parsedStartOfWeek = parseInt((_s = data.start_of_week) !== null && _s !== void 0 ? _s : '', 10);
        this.startOfWeek =
            Number.isNaN(parsedStartOfWeek) ||
                parsedStartOfWeek < 0 ||
                parsedStartOfWeek > 6
                ? 0
                : parsedStartOfWeek;
        this.deposit = {
            depositType: data.terms_deposit_type,
            noDepositCCCollect: parseRawBoolean(data.no_deposit_cc_collect),
        };
        this.formats = {
            decimalPoint: data.hotel_decimal_separator === undefined
                ? '.'
                : data.hotel_decimal_separator,
            defaultTimezone: ((_t = data.formats) === null || _t === void 0 ? void 0 : _t.default_timezone) || 'UTC',
            thousandsSeparator: data.hotel_thousand_separator === undefined
                ? ','
                : data.hotel_thousand_separator,
        };
        this.roomsSourceSettings = [];
        if ((_u = data.settings) === null || _u === void 0 ? void 0 : _u.roomTypeBookingSettings) {
            for (let i = 0; i < ((_v = data.settings) === null || _v === void 0 ? void 0 : _v.roomTypeBookingSettings.length); i++) {
                const setting = (_w = data.settings) === null || _w === void 0 ? void 0 : _w.roomTypeBookingSettings[i];
                this.roomsSourceSettings.push({
                    bookableLimit: parseInt(setting.bookableLimit),
                    canSelectSpecificRoom: parseRawBoolean(setting.bookRoomsOnMyBookings),
                    capacity: parseInt(setting.capacity),
                    maxRooms: parseInt(setting.maxRooms),
                    roomTypeId: setting.roomTypeId,
                    title: setting.title,
                });
            }
        }
        this.amplifyAllowGA4PurchaseEvent =
            (_x = data.amplify_allow_ga4_purchase_event) !== null && _x !== void 0 ? _x : true;
        this.amplifyFeatures = data.amplify_features || [];
        // Default to activated when the field is absent so a property served by an
        // older backend (or a garbled response) is never falsely shown as disabled.
        this.immersiveExperienceActivated =
            data.immersive_experience_v2 === undefined
                ? true
                : parseRawBoolean(data.immersive_experience_v2);
    }
}
